import { AuthProvider } from 'react-admin';
import { API_BASE_URL} from './config';

interface LoginResponse {
    code: number;
    message: string;
    data: {
        user: {
            id: number;
            email: string;
            name: string;
            status: number;
            role: number;
            picUrl: string;
            gender: number;
            phone: string;
            birthDate: string;
            location: string;
            bio: string;
            objectId: number;
            vip: number;
            vipEndTime: number;
            sourceId: number;
            createTime: string;
            updateTime: string;
        };
        accessToken: string;
        refreshToken: string;
        expiresDateTime: number;        
    };
}

// Check if token is expired
const isTokenExpired = (): boolean => {
    const expiresDateTime = localStorage.getItem('expiresDateTime');
    if (!expiresDateTime) return true;
    
    const expiryTime = parseInt(expiresDateTime);
    const currentTime = Math.floor(Date.now() / 1000); // Convert to seconds
    
    return currentTime >= expiryTime;
};

// Refresh the access token
const refreshAccessToken = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
        const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: JSON.stringify({ refreshToken }),
        });

        const result: LoginResponse = await response.json();

        if (result.code === 200 && result.data) {
            localStorage.setItem('accessToken', result.data.accessToken);
            localStorage.setItem('refreshToken', result.data.refreshToken);
            localStorage.setItem('expiresDateTime', result.data.expiresDateTime.toString());
            localStorage.setItem('user', JSON.stringify(result.data.user));
            return true;
        }
        return false;
    } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
    }
};

// Get valid access token (refresh if needed)
export const getAccessToken = async (): Promise<string | null> => {
    if (isTokenExpired()) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
            // Clear all auth data if refresh fails
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('expiresDateTime');
            localStorage.removeItem('user');
            return null;
        }
    }
    return localStorage.getItem('accessToken');
};

const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/auth/admin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: username,
                    password: password,
                }),
            });

            const result: LoginResponse = await response.json();

            if (result.code === 200 && result.data) {
                localStorage.setItem('accessToken', result.data.accessToken);
                localStorage.setItem('refreshToken', result.data.refreshToken);
                localStorage.setItem('expiresDateTime', result.data.expiresDateTime.toString());
                // Determine if user is super admin
                const isSuperAdmin = result.data.user.email === 'admin@kivoai.io';
                localStorage.setItem('isSuperAdmin', isSuperAdmin.toString());
                localStorage.setItem('user', JSON.stringify(result.data.user));
                return Promise.resolve();
            }

            return Promise.reject(new Error(result.message || 'Login failed'));
        } catch (error) {
            return Promise.reject(error);
        }
    },
    logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('expiresDateTime');
        localStorage.removeItem('user');
        localStorage.removeItem('isSuperAdmin');
        return Promise.resolve();
    },
    checkError: (error) => {
        const status = error.status;
        if (status === 401 || status === 403) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('expiresDateTime');
            localStorage.removeItem('user');
            localStorage.removeItem('isSuperAdmin');
            return Promise.reject();
        }
        return Promise.resolve();
    },
    checkAuth: async () => {
        const token = await getAccessToken();
        return token ? Promise.resolve() : Promise.reject();
    },
    getPermissions: () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            return Promise.resolve(user.role);
        }
        return Promise.resolve();
    },
    getIdentity: async () => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            const token = await getAccessToken();
            const avatarUrl = user.picUrl && token
                ? `${API_BASE_URL}/r2/download${user.picUrl}?authorization=${token}`
                : undefined;
            
            // Use email prefix if name is empty
            const fullName = user.name || (user.email ? user.email.split('@')[0] : 'User');
            
            return Promise.resolve({
                id: user.id.toString(),
                fullName: fullName,
                avatar: avatarUrl,
            });
        }
        return Promise.reject();
    },
};

export default authProvider;
